import { computed } from "vue";

import { useMediaQuery } from "@/lib/composables/media-query";
import { useStore as useUIStateStore } from "@/store/stores/ui-states";

const QUERY_PARAMETER = "show_preview";
const BREAKPOINT = "(width >= 1024px)";

// `?show_preview=...` pins the preview into a fixed visibility — used by
// the playground's preview-only embed and similar embedded modes.
const readOverride = (): boolean | undefined => {
  const value = new URLSearchParams(window.location.search).get(
    QUERY_PARAMETER
  );
  switch (value) {
    case "true":
    case "only:email":
    case "only:web": {
      return true;
    }
    case "false": {
      return false;
    }
    default: {
      return undefined;
    }
  }
};

// Toggle for the preview pane / drawer. Mirrors the sidebar pattern:
// desktop state persists across sessions (uiStates.isEmailPreviewOpen);
// mobile state is ephemeral (uiStates.isMobileEmailPreviewOpen) so the
// drawer doesn't pop open on every fresh mobile load. Viewport picks
// which one is active.
//
// `?show_preview=...` seeds the initial state on mount but does not lock
// it — the user's clicks always win after that. Re-reading on each mount
// means SPA navigation to a page with the override still applies it.
export const useShowingPreview = () => {
  const uiStates = useUIStateStore();
  const isWideEnough = useMediaQuery(BREAKPOINT);

  const override = readOverride();
  if (override !== undefined) {
    // Write to both flags so a later viewport flip keeps the override.
    uiStates.isEmailPreviewOpen = override;
    uiStates.isMobileEmailPreviewOpen = override;
  }

  return computed({
    get() {
      return isWideEnough.value
        ? uiStates.isEmailPreviewOpen
        : uiStates.isMobileEmailPreviewOpen;
    },
    set(next) {
      if (isWideEnough.value) {
        uiStates.isEmailPreviewOpen = next;
      } else {
        uiStates.isMobileEmailPreviewOpen = next;
      }
    },
  });
};
